import React, { useEffect, useState } from "react";
import { FormikProvider, useFormik } from "formik";
import { Banner, BannerProps, Spinner } from "kubra-ux-forge";
import { useTranslation } from "react-i18next";
import { PAYABLE_DETAILS_NAMESPACE } from "constants/i18n-namespaces";
import { useNavigate } from "react-router";
import routing from "routing";
import ContinueButton from "components/ezpay/payables/continue-button/continue-button";
import { ContactAndTocFooter } from "../../common/contact-and-toc-footer";
import { useDispatch, useSelector } from "react-redux";
import { getBiller } from "selectors";
import { useLabelsWithKrn } from "hooks";
import { getDomain, getLabelWithKrn } from "utilities/helper";
import { getBannerRole } from "utilities/banner-accessibility";
import {
  getPayableFromSession,
  postExtensionFields,
  postPayableInitiateIntent,
} from "api/IamApi/entities/ezpay";
import { setPayableSession } from "actions/payableSessionActions";
import { getPayableSession } from "selectors/payableSession";
import { getDeviceFingerprint } from "selectors/appState";
import { PayableCardHeader } from "../payable-card-header";
import { generateValidationSchema } from "components/extension-fields/schema";
import { Field } from "components/extension-fields/Field";
import {
  getDefaultValues,
  prepareExtensionFieldsPayload,
} from "components/extension-fields/utils";

export const PayableDetails: React.FC = () => {
  const biller = useSelector(getBiller);
  const fingerprint = useSelector(getDeviceFingerprint);

  const payableSession = useSelector(getPayableSession);
  const selectedPayableType = payableSession?.payableType;
  const selectedPayable = payableSession?.payable;

  const [loading, setLoading] = useState(false);
  const { getLabelsWithKrn } = useLabelsWithKrn();
  const labelSourceWithKrn = getLabelsWithKrn();
  const domain = getDomain();
  const { t, i18n } = useTranslation(PAYABLE_DETAILS_NAMESPACE);
  const navigate = useNavigate();
  const [bannerState, setBannerState] = useState<BannerProps>({
    active: false,
    variant: "error",
    messageLabel: "",
  });
  const dispatch = useDispatch();

  useEffect(() => {
    // scroll to top when banner shows up
    if (bannerState.active) {
      // scrolls to top in Safari
      document.body.scrollTop = 0;
      // scrolls to top in Chrome, Firefox, IE, and Opera
      document.documentElement.scrollTop = 0;
      // Mobile view
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [bannerState.active]);

  const saveExtensionFields = async (
    payableKrn: string,
    extensionFieldsRequest: unknown,
  ) => {
    if (loading) return;

    setLoading(true);
    try {
      await postExtensionFields(payableKrn, extensionFieldsRequest);
      await postPayableInitiateIntent(
        payableKrn,
        fingerprint,
        navigator.userAgent,
      );

      // TODO: review and see if it makes sense to have the /payable/initiate endpoint return the payable session object.
      const updatedSession = await getPayableFromSession(domain);
      dispatch(setPayableSession(updatedSession));

      navigate(routing.payableDashboard.index());
    } catch (error: any) {
      let messageLabel = t("generic-lookup-error");
      let messageBody = undefined;

      if (
        error.errorMessageCode === 404 &&
        selectedPayableType?.category.toLowerCase() === "account"
      ) {
        messageLabel = t("invalid-entry");
        messageBody = t("validation-error", {
          billedName: biller?.billerName,
          contactNo: contactUs.properties.phone,
        });
      } else if (error.errorMessageCode === 404) {
        messageLabel = t("payable-lookup-error", {
          payableName: getLabelWithKrn(
            labelSourceWithKrn.labelsWithKrn,
            selectedPayable?.typeKrn ?? null,
            selectedPayableType.name,
          ),
        });
        messageBody = undefined;
      }

      setBannerState({
        active: true,
        variant: "error",
        messageLabel,
        messageBody,
        withClose: true,
        withIcon: true,
        withHyperlink: false,
      });
    } finally {
      setLoading(false);
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: getDefaultValues(
      selectedPayableType.extensionFields,
      selectedPayable?.extensionFields,
    ),
    validationSchema: generateValidationSchema(
      selectedPayableType?.extensionFields ?? [],
      t,
      labelSourceWithKrn.labelsWithKrn,
      selectedPayable?.typeKrn ?? null,
    ),
    onSubmit: (values) => {
      const extensionFieldsRequest = prepareExtensionFieldsPayload(
        selectedPayableType.extensionFields,
        values,
      );

      if (!selectedPayable?.krn) {
        console.error("selectedPayable.krn is undefined");
        return;
      }

      saveExtensionFields(selectedPayable?.krn, extensionFieldsRequest);
      formik.setSubmitting(false);
    },
  });

  if (!selectedPayableType && !selectedPayable) {
    return (
      <div className="validate-frame">
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            marginTop: "10px",
          }}
        >
          <Spinner size="large" variant="primary" />
        </div>
      </div>
    );
  }

  const contactUs: any = biller?.properties?.find(
    (element: any) => element.name === "InstanceConfiguration",
  );

  const extensionFieldsDetailsLabel = getLabelWithKrn(
    labelSourceWithKrn.labelsWithKrn,
    selectedPayable?.typeKrn ?? null,
    "extensionFieldsDetailsLabel",
  );
  const isExtensionFieldsDetailsLabelAvailable =
    extensionFieldsDetailsLabel !== "extensionFieldsDetailsLabel";

  return (
    <div>
      {bannerState.active && (
        <div
          className="ez-pay-payable-banner"
          role={getBannerRole(bannerState.variant)}
        >
          <Banner
            {...bannerState}
            data-cy={
              bannerState?.variant
                ? `banner-${bannerState.variant}-content`
                : "banner-info-content"
            }
            onClose={() =>
              setBannerState((prev) => ({ ...prev, active: false }))
            }
          ></Banner>
        </div>
      )}
      <div className="ez-pay-payable-frame">
        <PayableCardHeader />

        <FormikProvider value={formik}>
          <form
            onSubmit={formik.handleSubmit}
            key={i18n.language}
            className="form-container"
          >
            <div className="ez-pay-payable-row">
              <div style={{ textAlign: "left", fontWeight: 700 }}>
                {isExtensionFieldsDetailsLabelAvailable
                  ? extensionFieldsDetailsLabel
                  : t("enter-payable-details")}
              </div>
            </div>

            {selectedPayableType?.extensionFields?.map((field) => (
              <Field
                key={field.name}
                fieldDefinition={field}
                typeKrn={selectedPayable?.typeKrn ?? null}
              />
            ))}
            <ContinueButton
              loading={loading}
              onClick={() => formik.handleSubmit()}
            />
          </form>
        </FormikProvider>
        <ContactAndTocFooter biller={biller} />
      </div>
    </div>
  );
};

export default PayableDetails;
